import User from '@/api/resources/users';
import router from '@/routes';
import i18n from '../../plugins/i18n';

const state = {
  user: null,
  step: 0,
  emailSent: false,
  accepted_with_success: false,
};

const getters = {
  currentUser: () => (state.user),
  getStepUser: () => (state.step),
  getEmailSent: () => (state.emailSent),
  getAcceptedWithSuccess: () => (state.accepted_with_success),
};

const checkIfNeedsTwoFactor = (twoFactorLoginAt) => {
  if (!twoFactorLoginAt) return true;

  const lastTwoFactorDate = new Date(twoFactorLoginAt);
  const now = new Date();
  const daysDifference = Math.floor((now - lastTwoFactorDate) / (1000 * 60 * 60 * 24));

  return daysDifference > 30;
};

const actions = {
  login({ commit, dispatch }, payload) {
    User.login(payload).then((response) => {
      const needsTwoFactor = checkIfNeedsTwoFactor(response.two_factor_verified_at);

      if (needsTwoFactor) {
        dispatch('removeLoading');

        router.push({
          name: 'two-factor-auth',
          params: {
            email: response.email,
            userId: response.id,
          },
        });
        return;
      }

      dispatch('createSession', response);
      dispatch('setKindBusiness', response.businesses[0]);
      commit('setUser', response);
      dispatch('removeLoading');
    });
  },

  recoverPassword({ dispatch }, payload) {
    User.recoverPassword(payload).finally(() => {
      dispatch('removeLoading');
      dispatch('addSuccessMessage', i18n.t('success.email_sent'));
    });
  },

  fbLogin({ commit, dispatch }, payload) {
    User.fbLogin(payload).then((response) => {
      dispatch('createSession', response);
      commit('setUser', response);
      dispatch('removeLoading');
    });
  },

  googleLogin({ commit, dispatch }, payload) {
    User.googleLogin(payload).then((response) => {
      dispatch('createSession', response);
      commit('setUser', response);
      dispatch('removeLoading');
    });
  },

  appleLogin({ commit, dispatch }, payload) {
    User.appleLogin(payload).then((response) => {
      dispatch('createSession', response);
      commit('setUser', response);
      dispatch('removeLoading');
    });
  },

  refreshUser({ commit, dispatch }) {
    User.show().then((response) => {
      dispatch('refreshBusiness', response.businesses[0]);
      dispatch('createSession', response);
      commit('setUser', response);
      dispatch('removeLoading');
    });
  },

  acceptCurrentTerms({ dispatch, commit }, payload) {
    dispatch('initLoading');

    return User.acceptTerms(payload).then(() => {
      commit('setUser', payload);
      commit('setAcceptedWithSuccess', true);
    });
  },

  acceptCurrentPrivacyPolicy({ dispatch, commit }, payload) {
    dispatch('initLoading');

    return User.acceptPrivacyPolicy(payload).then(() => {
      commit('setUser', payload);
      commit('setAcceptedWithSuccess', true);
    });
  },

  sendEmailToken({ dispatch, commit }, payload) {
    User.createEmailToken(payload).then(() => {
      dispatch('removeLoading');
      commit('setEmailSent', true);
      dispatch('addSuccessMessage', i18n.t('success.email_sent'));
    });
  },

  validateEmailToken({ dispatch, commit }, payload) {
    User.validateEmailToken(payload).then((response) => {
      dispatch('removeLoading');
      commit('setUser', response);
    });
  },

  register({ commit, dispatch }, payload) {
    User.register(payload).then((response) => {
      commit('setUser', response);
      dispatch('createSession', response);
      dispatch('removeLoading');
      commit('nextStep');
    });
  },

  updateUser({ commit, dispatch }, payload) {
    User.update(payload).then((response) => {
      commit('setUser', response);
      dispatch('removeLoading');
      dispatch('addSuccessMessage', i18n.t('success.user_updated'));
    });
  },

  logout({ commit, dispatch }) {
    commit('setUser', null);
    dispatch('destroySession');
  },

  setStepUser({ commit }) {
    commit('setStep');
  },

  nextStepUser({ commit }) {
    commit('nextStep');
  },
  backStepUser({ commit }) {
    commit('backStep');
  },

  sendTwoFactorCode({ dispatch }, payload) {
    return User.sendTwoFactorCode(payload).then((response) => {
      dispatch('removeLoading');
      return response;
    }).catch((error) => {
      dispatch('removeLoading');
      throw error;
    });
  },

  verifyTwoFactorCode({ commit, dispatch }, payload) {
    return User.verifyTwoFactorCode(payload).then((response) => {
      dispatch('createSession', response);
      dispatch('setKindBusiness', response.businesses[0]);
      commit('setUser', response);
      dispatch('removeLoading');
      return response;
    }).catch((error) => {
      dispatch('removeLoading');
      throw error;
    });
  },
};

const mutations = {
  setUser($state, payload) {
    const stateCopy = $state;
    stateCopy.user = payload;
  },
  setAcceptedWithSuccess($state, payload) {
    const stateCopy = $state;
    stateCopy.accepted_with_success = payload;
  },
  nextStep($state) {
    const stateCopy = $state;
    stateCopy.step += 1;
  },
  backStep($state) {
    const stateCopy = $state;
    stateCopy.step -= 1;
  },
  setStep($state) {
    const stateCopy = $state;
    stateCopy.step = 0;
  },
  setEmailSent($state) {
    const stateCopy = $state;
    stateCopy.emailSent = true;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
